import React from 'react'
import { When } from 'react-if'
import { Button, LinkButton } from '@/atoms/Button'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { CopyIcon } from '@/atoms/Icons/CopyIcon'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { HeadingLG, ParagraphMD, ParagraphXL } from '@/atoms/Text'
import { Modal } from '@/molecules/Modal'
import { CatalogTitle } from '@/services/ContentCatalog'
import { useUser } from '@/services/UserService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useLoginUrl } from '@/utils/auth-utils'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

interface Props {
  showModal: boolean
  handleModalClose: () => void
  createCopyLink: () => void
  displayName: string
  moviePosterPath?: string | null
  catalogTitle?: CatalogTitle
  shareUrl: string
  handleLinkCopied?: () => void
  isCreateLoading?: boolean
  linkError: string | null
}

export const OneToManyModal: React.FC<Props> = ({
  showModal,
  handleModalClose,
  createCopyLink,
  displayName,
  moviePosterPath,
  catalogTitle,
  shareUrl,
  handleLinkCopied,
  isCreateLoading = false,
  linkError,
}) => {
  const { t } = useTranslate('watch')
  const { isLoggedIn } = useUser()
  const { loginUrl, trackLoginStarted } = useLoginUrl()
  const projectName = catalogTitle?.title
  const hasShareUrl = Boolean(shareUrl && shareUrl.length > 0)

  return (
    <Modal
      isOpen={showModal}
      onClose={handleModalClose}
      panelClasses="md:min-w-[88vw] lg:min-w-min !p-4 !pb-8 !max-w-[834px]"
      className=""
    >
      <div className="flex flex-col">
        <div className="flex items-center justify-end">
          <div onClick={handleModalClose} className="cursor-pointer">
            <CloseIcon color="black" size={32} className="cursor-pointer" />
          </div>
        </div>
        <div className="px-6">
          <div className="flex flex-col gap-4">
            <HeadingLG weight="bold">{t('shareGoodnessWithFriend', 'Share this goodness with a friend!')}</HeadingLG>
            <ParagraphXL color="core-gray-700">
              {t(
                'sendALinkHelpAFriendManyLinks',
                'As a Guild member, you can send a link and help a friend or loved one enjoy this title for free. As a Guild member you can share as many links as you would like. Share now!',
              )}
            </ParagraphXL>
            <div className="flex w-full flex-row gap-8 rounded-2xl bg-core-gray-100 px-14 py-10">
              <div>
                {moviePosterPath && (
                  <div className="block overflow-hidden rounded-lg">
                    <img
                      src={getCloudinaryImageUrl({ path: moviePosterPath, width: 150, height: 225 })}
                      className="min-h-[225px] min-w-[150px]"
                      alt={t('moviePosterAlt', '{{name}} Movie Poster', { name: projectName })}
                      height={225}
                      width={150}
                    />
                  </div>
                )}
              </div>
              <div className="w-full">
                {!!displayName && (
                  <div className="flex flex-row gap-1">
                    <ParagraphXL weight="bold">{t('from', 'From')}:</ParagraphXL>
                    <ParagraphXL weight="medium">{displayName}</ParagraphXL>
                  </div>
                )}
                <When condition={!isLoggedIn}>
                  <div className="mt-8 max-h-[60px]">
                    <div>
                      <Translate t={t} i18nKey="logInToShareDetails" values={{ projectName }}>
                        Log in now to share {{ projectName }} with your friends and family for free!
                      </Translate>
                    </div>
                    <div className=" mt-4 w-fit">
                      <LinkButton
                        href={loginUrl}
                        variant="core-gray-900"
                        className="m-0 min-w-fit rounded-lg px-6 py-4"
                        onClick={() => trackLoginStarted('one-to-many-sharing-modal')}
                      >
                        {t('logInToShareButton', 'Log In to Share')}
                      </LinkButton>
                    </div>
                  </div>
                </When>
                <When condition={isLoggedIn}>
                  <When condition={!hasShareUrl}>
                    <Button
                      variant="core-gray-900"
                      className="mt-8 min-w-[148px] px-7 py-[17px]"
                      onClick={createCopyLink}
                    >
                      <When condition={isCreateLoading}>
                        <LoadingSpinnerIcon />
                      </When>
                      <When condition={!isCreateLoading}>{t('createCopyLink', 'Create and Copy Link')}</When>
                    </Button>
                  </When>
                  <When condition={hasShareUrl}>
                    <div className="mt-8 flex max-h-[60px] flex-row">
                      <div className="flex max-w-[320px] items-center justify-start overflow-hidden text-nowrap rounded-l-lg border-[1.5px] border-core-gray-300 bg-white px-4 text-core-gray-950">
                        {shareUrl}
                      </div>
                      <Button
                        variant="core-gray-900"
                        className="m-0 min-w-fit rounded-l-none rounded-r-lg px-3 py-[17px]"
                        onClick={handleLinkCopied}
                      >
                        <CopyIcon color="white" size={32} className="mr-1.5" />
                        {t('copyLink', 'Copy Link')}
                      </Button>
                    </div>
                  </When>
                  <When condition={!!linkError}>
                    <ParagraphMD color="error-500">{linkError}</ParagraphMD>
                  </When>
                </When>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}
